import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import WhatsAppButton from '../components/WhatsAppButton';
import EmailButton from '../components/EmailButton';
import { jobBySlug, jobPath, jobs } from '../data/jobs';

// A page for one real job: what was done, in Romeo's photos, with each
// photo's caption as its alt text so search engines can read the pictures.
const JobPage = ({ slug }) => {
  const job = jobBySlug(slug);
  const where = job.town ? ` in ${job.town}, Kent` : ' in Kent';
  const kind = job.category.toLowerCase();
  const others = jobs.filter((j) => j.slug !== job.slug);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${job.title}${where} | Paint It Better`}
        description={`${job.category} job${where} by Paint It Better: ${job.summary.split('. ')[0]}.`}
        canonical={jobPath(job)}
        image={job.photos[0].src}
      />

      {/* Hero */}
      <section className="relative pt-36 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-cyan-300 font-medium mb-4">
              <MapPin className="h-5 w-5" /> {job.category}{where}
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              {job.title}{job.town ? ` in ${job.town}` : ''}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">{job.summary}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6 h-9">
                  Free Quote
                </Button>
              </Link>
              <WhatsAppButton />
              <EmailButton />
            </div>
          </div>
        </div>
      </section>

      {/* Photos */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">The job in photos</h2>
          <p className="text-slate-600 mb-8">From the finished work, through the job in progress, to how it looked before.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {job.photos.map((p) => (
              <figure key={p.src} className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                <img src={p.src} alt={p.caption || job.title} loading="lazy" className="w-full h-64 object-cover" />
                {p.caption && <figcaption className="p-4 text-sm text-slate-600">{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Next step */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Planning something similar?</h2>
          <p className="text-slate-600 mb-6">
            See what our {kind} covers, or send us a few photos for a free written quote.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to={`/services#${job.serviceAnchor}`} className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
              Our {kind} service <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
              Get a free quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* More jobs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">More of our work</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((j) => (
              <Link key={j.slug} to={jobPath(j)} className="group rounded-2xl overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow">
                <img src={j.photos[0].src} alt={j.photos[0].caption || j.title} loading="lazy" className="w-full h-48 object-cover" />
                <div className="p-4">
                  <p className="text-sm text-cyan-700 font-medium">{j.category}</p>
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-cyan-700">{j.title}</h3>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
              See the full portfolio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobPage;
